import React, { useState, useEffect } from "react";
import {
  Save, AlertCircle, CheckCircle, Loader2, RefreshCw
} from "lucide-react";
import { Button } from "../../components/ui/Button";
import { Card } from "../../components/ui/card";
import {
  createQuoteFormSettings,
  updateQuoteFormSettings,
  getQuoteFormSettings
} from "../../redux/Actions/QuoteFormSettingsActions";
import {
  QuoteFormSettingsCreateDto,
  QuoteFormSettings as QuoteFormSettingsType
} from "../../modules/shared/model/quoteFormSettings.interfaces";

const QuoteFormSettings: React.FC = () => {
  const [settings, setSettings] = useState<QuoteFormSettingsType | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<"idle" | "success" | "error">("idle");

  // Form state
  const [formData, setFormData] = useState({
    emergencyDepositPercentage: null,
    emergencyResponseTime: null,
    emergencyUrgencyMultiplier: false,
    emergencyLocationVerification: false,
    regularValidity: null,
    regularMaximumRequest: null,
    regularComparison: false,
    regularNegotiation: false,
    includeTravelPrice: false,
    applyPeakHours: false,
    enableMilestonePayment: false
  });

  // Load existing settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getQuoteFormSettings();
      if (response.data && response.data.length > 0) {
        const existingSettings = response.data[0];
        setSettings(existingSettings);
        // Populate form with existing data
        setFormData({
          emergencyDepositPercentage: existingSettings.emergencyDepositPercentage,
          emergencyResponseTime: existingSettings.emergencyResponseTime,
          emergencyUrgencyMultiplier: existingSettings.emergencyUrgencyMultiplier,
          emergencyLocationVerification: existingSettings.emergencyLocationVerification,
          regularValidity: existingSettings.regularValidity,
          regularMaximumRequest: existingSettings.regularMaximumRequest,
          regularComparison: existingSettings.regularComparison,
          regularNegotiation: existingSettings.regularNegotiation,
          includeTravelPrice: existingSettings.includeTravelPrice,
          applyPeakHours: existingSettings.applyPeakHours,
          enableMilestonePayment: existingSettings.enableMilestonePayment
        });
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: checked
    }));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    setSaveStatus("idle");
    setError(null);

    try {
      const settingsData: QuoteFormSettingsCreateDto = {
        regularMaximumRequest: formData.regularMaximumRequest,
        regularValidity: formData.regularValidity,
        regularNegotiation: formData.regularNegotiation,
        regularComparison: formData.regularComparison,
        includeTravelPrice: formData.includeTravelPrice,
        applyPeakHours: formData.applyPeakHours,
        enableMilestonePayment: formData.enableMilestonePayment,
        emergencyLocationVerification: formData.emergencyLocationVerification,
        emergencyUrgencyMultiplier: formData.emergencyUrgencyMultiplier,
        emergencyResponseTime: formData.emergencyResponseTime,
        emergencyDepositPercentage: formData.emergencyDepositPercentage
      };

      let updatedSettings: QuoteFormSettingsType;
      
      if (settings) {
        // Update existing settings
        updatedSettings = await updateQuoteFormSettings(settings.id, settingsData);
      } else {
        // Create new settings
        updatedSettings = await createQuoteFormSettings(settingsData);
      }

      // Update local state
      setSettings(updatedSettings);
      setSaveStatus("success");
      
      // Clear success status after 3 seconds
      setTimeout(() => setSaveStatus("idle"), 3000);

    } catch (error: any) {
      console.error("Failed to save quote form settings:", error);
      setError(error?.message || "Failed to save settings");
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefaults = () => {
    setFormData({
      emergencyDepositPercentage: 20,
      emergencyResponseTime: 30,
      emergencyUrgencyMultiplier: true,
      emergencyLocationVerification: true,
      regularValidity: 7,
      regularMaximumRequest: 5,
      regularComparison: true,
      regularNegotiation: true,
      includeTravelPrice: true,
      applyPeakHours: true,
      enableMilestonePayment: true
    });
  };

  if (loading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          <span className="ml-2 text-gray-600">Loading settings...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 space-y-6">
      {/* Status Messages */}
      {saveStatus === "success" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-2">
          <CheckCircle className="h-5 w-5 text-green-600" />
          <span className="text-green-700">Quote form settings saved successfully!</span>
        </div>
      )}

      {saveStatus === "error" && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-700">Failed to save settings. Please try again.</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <span className="text-red-700">{error}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setError(null)}
            className="ml-auto"
          >
            Dismiss
          </Button>
        </div>
      )}

      <div>
        <h2 className="text-lg font-semibold mb-4">Quote Form Settings</h2>
        <div className="space-y-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-blue-700">
                These settings affect all quote request forms across the platform.
              </p>
            </div>
          </div>

          {/* Emergency Quote Settings */}
          <div>
            <h3 className="font-medium mb-4">Emergency Quote Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Default Deposit Percentage
                </label>
                <input
                  type="number"
                  value={formData.emergencyDepositPercentage}
                  onChange={(e) => handleInputChange("emergencyDepositPercentage", parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  min="0"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Response Time Limit (minutes)
                </label>
                <input
                  type="number"
                  value={formData.emergencyResponseTime}
                  onChange={(e) => handleInputChange("emergencyResponseTime", parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  min="1"
                />
              </div>
            </div>
            <div className="mt-3 sm:mt-4 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.emergencyUrgencyMultiplier}
                  onChange={(e) => handleCheckboxChange("emergencyUrgencyMultiplier", e.target.checked)}
                  className="rounded"
                />
                <span>Enable Urgency Multiplier</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.emergencyLocationVerification}
                  onChange={(e) => handleCheckboxChange("emergencyLocationVerification", e.target.checked)}
                  className="rounded"
                />
                <span>Require Location Verification</span>
              </label>
            </div>
          </div>

          {/* Regular Quote Settings */}
          <div>
            <h3 className="font-medium mb-4">Regular Quote Settings</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quote Validity Period (days)
                </label>
                <input
                  type="number"
                  value={formData.regularValidity}
                  onChange={(e) => handleInputChange("regularValidity", parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Maximum Quote Requests
                </label>
                <input
                  type="number"
                  value={formData.regularMaximumRequest}
                  onChange={(e) => handleInputChange("regularMaximumRequest", parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded-md text-sm"
                  min="1"
                />
              </div>
            </div>
            <div className="mt-3 sm:mt-4 space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.regularComparison}
                  onChange={(e) => handleCheckboxChange("regularComparison", e.target.checked)}
                  className="rounded"
                />
                <span>Enable Quote Comparison</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.regularNegotiation}
                  onChange={(e) => handleCheckboxChange("regularNegotiation", e.target.checked)}
                  className="rounded"
                />
                <span>Allow Price Negotiation</span>
              </label>
            </div>
          </div>

          {/* Price Calculation Rules */}
          <div>
            <h3 className="font-medium mb-4">Price Calculation Rules</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.includeTravelPrice}
                  onChange={(e) => handleCheckboxChange("includeTravelPrice", e.target.checked)}
                  className="rounded"
                />
                <span>Include Travel Distance in Price</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.applyPeakHours}
                  onChange={(e) => handleCheckboxChange("applyPeakHours", e.target.checked)}
                  className="rounded"
                />
                <span>Apply Peak Hour Rates</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={formData.enableMilestonePayment}
                  onChange={(e) => handleCheckboxChange("enableMilestonePayment", e.target.checked)}
                  className="rounded"
                />
                <span>Enable Milestone Payments</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="pt-4 border-t flex justify-between">
        <Button
          variant="outline"
          onClick={handleResetToDefaults}
          className="flex items-center gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          Reset to Defaults
        </Button>
        <Button
          onClick={handleSaveSettings}
          disabled={saving}
          className="flex items-center gap-2"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4" />
              Save Settings
            </>
          )}
        </Button>
      </div>
    </Card>
  );
};

export default QuoteFormSettings;
